/**
 * Auth Store
 * 
 * Zustand store for authentication state management.
 * Follows SOLID principles with clear separation of concerns.
 */

import { authService } from "@/services/auth.service";
import queryClient from "@/lib/tanstackQuery";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { AuthStore } from "./types";

/**
 * Authentication store using Zustand
 * 
 * Features:
 * - Persistent authentication state
 * - Integration with authService for token management
 * - DevTools support for debugging
 * - Type-safe actions and state
 */
export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => ({
      // Initial state - check if user has valid token
      isAuthenticated: !!authService.getAccessToken(),
      user: authService.getUser(),

      // Actions
      login: (user, accessToken: string, refreshToken?: string) => {
        authService.setAccessToken(accessToken);
        authService.setUser(user);
        if (refreshToken) {
          authService.setRefreshToken(refreshToken);
        }
        set({ isAuthenticated: true, user }, false, "auth/login");
      },

      logout: () => {
        authService.triggerLogout();
        set({ isAuthenticated: false, user: null }, false, "auth/logout");
      },

      setAuthenticated: (value: boolean) => {
        set({ isAuthenticated: value }, false, "auth/setAuthenticated");
      },

      setUser: (user) => {
        if (user) authService.setUser(user);
        set({ user }, false, "auth/setUser");
      },
    }),
    {
      name: "auth-store",
      enabled: import.meta.env.DEV, // Enable DevTools only in development
    }
  )
);

/**
 * Setup auth service logout handler
 * This ensures the store stays in sync when authService triggers logout
 */
authService.onLogout(() => {
  useAuthStore.getState().setAuthenticated(false);
  useAuthStore.setState({ user: null });
  queryClient.clear();
});
